import {
  createStore,
  emitOnce,
  filterNil,
  select,
  withProps,
} from '@ngneat/elf';
import {
  getAllEntities,
  selectAllEntities,
  setEntities,
  withEntities,
} from '@ngneat/elf-entities';
import {
  clearRequestsStatus,
  createRequestsStatusOperator,
  selectRequestStatus,
  updateRequestStatus,
  withRequestsStatus,
} from '@ngneat/elf-requests';
import { AvailableRegions, MapSet } from '@tmx/data-access';
import { RowsDescriptor, SortDirection } from '@tmx/operandi';
import { Subject } from 'rxjs';

import { EMPTY_ROWS_DESCRIPTOR } from '../../../../../models/empty-rows-descriptor';

interface mapSetsProps {
  activeMapSet: MapSet | null;
  activeMapSetVehicleGroupIds: number[];
  availableRegions: AvailableRegions | null;
  creatingNewMapSet: boolean;
  hasMapSets: boolean;
  mapSetErrorMessage: null | string;
  mapSetRowsDescriptor: RowsDescriptor;
  maxMapSetAssignments: number;
  offset: number;
  query: string;
  sortDirection: '' | 'asc' | 'desc';
  sortExpression: string;
  totalMapSets: number;
}

const mapSetsStore = createStore(
  { name: 'mapSets' },
  withProps<mapSetsProps>({
    maxMapSetAssignments: 0,
    hasMapSets: false,
    offset: 0,
    query: '',
    sortExpression: '',
    sortDirection: SortDirection.None,
    mapSetRowsDescriptor: EMPTY_ROWS_DESCRIPTOR,
    totalMapSets: 0,
    activeMapSet: null,
    mapSetErrorMessage: null,
    activeMapSetVehicleGroupIds: [],
    creatingNewMapSet: false,
    availableRegions: null,
  }),
  withEntities<MapSet, 'customMapsetId'>({
    idKey: 'customMapsetId',
  }),
  withRequestsStatus<
    | 'available-regions'
    | 'delete-map-set'
    | 'map-set-details'
    | 'map-set-settings'
    | 'map-set-update'
    | 'map-set-vehicle-groups'
    | 'map-set-vehicle-groups-assign'
    | 'map-sets'
  >()
);

export const trackMapsetsRequestsStatus =
  createRequestsStatusOperator(mapSetsStore);

export const mapSets$ = mapSetsStore.pipe(selectAllEntities());

export const mapSetSettingsStatus$ = mapSetsStore.pipe(
  selectRequestStatus('map-set-settings')
);

export const deleteMapSetStatus$ = mapSetsStore.pipe(
  selectRequestStatus('delete-map-set')
);

export const mapSetsStatus$ = mapSetsStore.pipe(
  selectRequestStatus('map-sets')
);

export const mapSetUpdateStatus$ = mapSetsStore.pipe(
  selectRequestStatus('map-set-update')
);

export const mapSetDetailsStatus$ = mapSetsStore.pipe(
  selectRequestStatus('map-set-details')
);

export const mapSetVehicleGroupsStatus$ = mapSetsStore.pipe(
  selectRequestStatus('map-set-vehicle-groups')
);

export const mapSetVehicleGroupsAssignStatus$ = mapSetsStore.pipe(
  selectRequestStatus('map-set-vehicle-groups-assign')
);

export const availableRegionsStatus$ = mapSetsStore.pipe(
  selectRequestStatus('available-regions')
);

export const mapSetErrorMessage$ = mapSetsStore.pipe(
  select(({ mapSetErrorMessage }) => mapSetErrorMessage),
  filterNil()
);

export const hasMapSets$ = mapSetsStore.pipe(
  select((state) => state.hasMapSets)
);

export const mapSetRowsDescriptor$ = mapSetsStore.pipe(
  select((state) => state.mapSetRowsDescriptor)
);

export const tableReload$ = new Subject<void>();
export const currentStepReset$ = new Subject<number>();
export const currentStepSave$ = new Subject<number>();

export function updateHasMapSets(hasMapSets: boolean) {
  mapSetsStore.update((state) => ({
    ...state,
    hasMapSets,
  }));
}

export function updateMapSets(mapSets: MapSet[], totalMapSets: number) {
  mapSetsStore.update(
    (state) => ({
      ...state,
      totalMapSets,
    }),
    setEntities(mapSets),
    updateRequestStatus('map-sets', 'success')
  );
}

export function updateMaxMapsetAssignments(maxMapSetAssignments: number) {
  mapSetsStore.update(
    (state) => ({
      ...state,
      maxMapSetAssignments,
    }),
    updateRequestStatus('map-set-settings', 'success')
  );
}

export function updateOffset(offset: number) {
  mapSetsStore.update((state) => ({
    ...state,
    offset,
  }));
}

export function updateSort(
  sortExpression: string,
  sortDirection: '' | 'asc' | 'desc'
) {
  mapSetsStore.update((state) => ({
    ...state,
    sortExpression,
    sortDirection,
  }));
}

export function updateQuery(query: string) {
  mapSetsStore.update((state) => ({
    ...state,
    query,
  }));
}

export function updateMapSetRowsDescriptor(
  mapSetRowsDescriptor: RowsDescriptor
) {
  mapSetsStore.update((state) => ({
    ...state,
    mapSetRowsDescriptor,
  }));
}

export function updateActiveMapSet(activeMapSet: MapSet | null) {
  mapSetsStore.update((state) => ({
    ...state,
    activeMapSet,
  }));
}

export function updateActiveMapSetVehicleGroupIds(
  activeMapSetVehicleGroupIds: number[]
) {
  mapSetsStore.update(
    (state) => ({
      ...state,
      activeMapSetVehicleGroupIds,
    }),
    updateRequestStatus('map-set-vehicle-groups', 'success')
  );
}

export function updateActiveMapSetDetails(activeMapSet: MapSet | null) {
  mapSetsStore.update(
    (state) => ({
      ...state,
      activeMapSet,
    }),
    updateRequestStatus('map-set-details', 'success')
  );
}

export function updateMapsetErrorMessage(mapSetErrorMessage: null | string) {
  mapSetsStore.update((state) => ({
    ...state,
    mapSetErrorMessage,
  }));
}

export function updateCreatingNewMapset(creatingNewMapSet: boolean) {
  mapSetsStore.update((state) => ({
    ...state,
    creatingNewMapSet,
  }));
}

export function updateAvailableRegions(availableRegions: AvailableRegions) {
  mapSetsStore.update(
    (state) => ({
      ...state,
      availableRegions,
    }),
    updateRequestStatus('available-regions', 'success')
  );
}

export function updateMapSetAssignVehicleGroupsStatus() {
  mapSetsStore.update(
    updateRequestStatus('map-set-vehicle-groups-assign', 'success')
  );
}

export function updateDeleteMapSetStatus() {
  mapSetsStore.update(updateRequestStatus('delete-map-set', 'success'));
}

export function updateTableReload() {
  tableReload$.next();
}

export function updateCurrentStepReset(currentStep: number) {
  currentStepReset$.next(currentStep);
}

export function updateCurrentStepSave(currentStep: number) {
  currentStepSave$.next(currentStep);
}

export function resetMapSetsStatus() {
  mapSetsStore.update(updateRequestStatus('map-sets', 'idle'));
}

export function updateMapSetUpdateStatus() {
  mapSetsStore.update(updateRequestStatus('map-set-update', 'success'));
}

export function clearMapSetsStore() {
  emitOnce(() => {
    mapSetsStore.update(clearRequestsStatus());
    mapSetsStore.reset();
  });
}

export const mapSetsEntities = () => mapSetsStore.query(getAllEntities());

export const mapSetsStoreSnapshot = () => mapSetsStore.getValue();
